import { PSTData } from '@ebiz/ember-aditools-device-table/utils/device-table-pst';
import Logger from '../services/logger';
import * as AditoolsSpice from '@ebiz/aditools-spice/schematics';
import * as AditoolsSpiceParts from '@ebiz/aditools-spice/data/opamps.json';

let interpretParameter = function (
  parameter: PSTParameter,
  datatype: DataType,
  datavalue: CardinalityType,
): [string | null, string | null] {
  if (
    typeof parameter.value === 'undefined' ||
    parameter.value === null ||
    typeof parameter.value.length === 'undefined'
  ) {
    return [null, 'INVALID'];
  }
  if (parameter.value.length === 0 || (parameter.value.length === 1 && parameter.value[0] === '')) {
    return [null, 'EMPTY'];
  }
  if (datatype === DataType.t_pulldown && datavalue === CardinalityType.c_multi) {
    return [parameter.value.join('; '), null];
  } else {
    return [parameter.value[0] as string, null];
  }
};

// This function determines which parameters are empty, missing, or invalid,
// whether they are optional and thus being missing or empty is okay,
// and whether the optional parameters have defaults.
let convertMergedPartToFlatHash = function (
  mergedPart: Record<string, PSTParameter>,
  parameters: PstParameterSet<string>,
  extras: string[],
  logging: Logger,
  logOmissions?: boolean,
): Record<string, string | Record<string, unknown>> | null {
  //TODO: Handle error case where there is no part value.

  const partId = mergedPart['partId'];
  const generic = mergedPart['generic'];
  // Fill in all the part's parameters listed in the table and enumerate what was invalid, empty, or simply missing.
  let part: Record<string, string | Record<string, unknown>> = {
    partNumber: partId && partId.value ? (partId.value[0] as string) : '',
    generic: generic && generic.value ? (generic.value[0] as string) : '',
  };
  let potentialErrors: { error: string; parminfo: PstParameterDescriptor<string> }[] = [];
  Object.values(parameters).forEach((parminfo) => {
    if (parminfo.id !== '0') {
      // Skip the name - we've already got it
      if (parminfo.id in mergedPart) {
        let [value, error] = interpretParameter(mergedPart[parminfo.id] as PSTParameter, parminfo.data, parminfo.value);
        if (error) {
          if (error === 'INVALID') {
            potentialErrors.push({ error: 'INVALID', parminfo: parminfo });
          } else if (error === 'EMPTY' && !('default' in parminfo)) {
            potentialErrors.push({ error: 'EMPTY', parminfo: parminfo });
          }
        } else {
          if (value !== null) {
            // this won't happen because the null value only happens on error
            part[parminfo.name] = value;
          }
        }
      } else {
        potentialErrors.push({ error: 'MISSING', parminfo: parminfo });
      }
    }
  });

  // Fill in the contents of the extra parameters as well - could be any hash
  for (let parm of ['spice'].concat(extras)) {
    let parmParts = parm.split(':');
    let outParm = parmParts[parmParts.length - 1] as string; //Same as inParm if there is no colon
    if (outParm in mergedPart) {
      part[outParm] = mergedPart[outParm] as PSTParameter;
    }
  }

  // We have all of the provided non-default parameters. Fill in the defaults for the part's parameters based on other parameters.
  // Notes:
  // * Default values should not be used to determine other default values, as there is no guarantee which default will be evaluated first.
  // * A part with a default value or function will only be disqualified based on that value
  //   if the default value is undefined _and_ the parameter is not optional. Until we process the optional state, it's only a potential error.
  Object.values(parameters).forEach((parminfo) => {
    if ('default' in parminfo && typeof parminfo.default !== 'undefined') {
      if (!(parminfo.name in part)) {
        let value = parminfo.default(part as Record<string, string>);
        if (typeof value === 'string') {
          part[parminfo.name] = value;
        } else {
          potentialErrors.push({ error: 'EMPTY', parminfo: parminfo });
        }
      }
    }
  });

  // Now we have all the available parameter values (including defaults) to use to determine which other parameters are optional.
  // We need to go through all our pending EMPTY or MISSING errors and see if the parameters involved are optional.
  // If a parameter is optional, it isn't an error if it is missing, so it isn't a reason to disqualify the part.
  let invalidParms: string[] = [];
  let missingParms: string[] = [];
  let emptyParms: string[] = [];
  for (let error of potentialErrors) {
    if (error.error === 'INVALID') {
      invalidParms.push(`${error.parminfo.name}(${error.parminfo.id})`);
    } else {
      let isOptional =
        typeof error.parminfo.optional === 'boolean'
          ? error.parminfo.optional
          : error.parminfo.optional(part as Record<string, string>);
      if (!isOptional) {
        if (error.error === 'EMPTY') {
          emptyParms.push(`${error.parminfo.name}(${error.parminfo.id})`);
        } else if (error.error === 'MISSING') {
          missingParms.push(`${error.parminfo.name}(${error.parminfo.id})`);
        }
      }
    }
  }

  // We're done - either deliver the part or (optionally) log the reasons we're not delivering it.
  if (missingParms.length > 0 || emptyParms.length > 0 || invalidParms.length > 0) {
    if (logOmissions) {
      let causes = [];
      if (invalidParms.length > 0) {
        causes.push(`invalid parms ${invalidParms.join(', ')}`);
      }
      if (emptyParms.length > 0) {
        causes.push(`empty parms ${emptyParms.join(', ')}`);
      }
      if (missingParms.length > 0) {
        causes.push(`missing parms ${missingParms.join(', ')}`);
      }
      const partId = mergedPart['partId'] as PSTParameter;
      logging.info(`Skipping ${partId.value} due to ${causes.join('; ')} `);
    }
    return null;
  }

  return part;
};

let interpretPartIndex = function (
  index: PartIndex,
  parameters: PstParameterSet<string>,
  extras: string[],
  logging: Logger,
  logOmissions?: boolean,
): PstDeviceList<string> | null {
  try {
    let parts: PstDeviceList<string> = [];
    index.getIds().forEach((partId) => {
      let convertedPart = convertMergedPartToFlatHash(
        index.getMergedPart(partId, extras),
        parameters,
        extras,
        logging,
        logOmissions,
      );
      if (convertedPart) {
        parts.push(convertedPart);
      }
    });
    return parts;
  } catch (error: unknown) {
    let namedError = error as { message: string };
    logging.warn('Failure occurred interpreting device data:', namedError.message);
    return null;
  }
};

// Collect the information from the config data into the Part Index, which organizes the data by part, source, and parameter, keeping all the sources separate.
let collectConfigData = function (
  adiLocale: string,
  config: ToolsConfigJson<unknown>,
  configParms: string[],
  index: PartIndex,
  staticParts?: StaticPartList,
): void {
  // Static data in a separate file
  if (config.cdpsource.useStaticPartsData && staticParts) {
    let staticPartList = staticParts.data[adiLocale] as PSTParameterSet[];
    staticPartList.forEach((part) => {
      index.addPart(part['0'] ? (part['0'].displayValue as string) : '', 'static', part);
    });
  }

  // Parts data from the config file
  if (config.cdpsource.useLocalPartsData) {
    let item = Object.entries(config.cdpsource.localParts).filter((item) => item[0] == adiLocale)[0];
    if (item) {
      item[1].forEach((part) => {
        index.addPart(part['partId'] as string, 'config', part as PSTParameterSet);
      });
    }
  }

  // Aditools Spice Parts
  const spiceParts: Record<string, AditoolsSpice.SpicePartInfo> = AditoolsSpiceParts;
  if (spiceParts) {
    Object.keys(spiceParts).forEach((id) => {
      index.addPart(id, 'spice', { value: spiceParts[id] !== undefined ? 'Yes' : 'No' } as PSTParameterSet);
    });
  }

  // Additional sections from the config file
  for (let parm of configParms) {
    let parmParts = parm.split(':');
    let inParm = parmParts[0] as string;
    //let outParm = parmParts[parmParts.length-1]; //Same as inParm if there is no colon
    let item = Object.entries(config).find((item) => item[0] == inParm) as [string, Record<string, unknown>];
    if (item) {
      Object.keys(item[1]).forEach((id) => {
        index.addPart(id, inParm, item[1][id] as PSTParameterSet);
      });
    }
  }
};

let collectPstData = function (ignoreRX: string, parts: PSTData, index: PartIndex): void {
  let ignoreRegEx = ignoreRX ? new RegExp(ignoreRX) : null;
  parts.data.forEach((part) => {
    if (!ignoreRegEx || !ignoreRegEx.test(part['0'].displayValue as string)) {
      index.addPart(part['0'].displayValue as string, 'pst', part as PSTParameterSet);
    }
  });
};

/// -------- Public interface --------

/// PST Parameter Metadata

export enum DataType {
  t_pulldown = 'Pulldown',
  t_float = 'Floating Point',
  t_integer = 'Integer',
  t_string = 'String',
  t_none = 'N/A',
}

export enum CardinalityType {
  c_single = 'single',
  c_multi = 'multi',
  c_none = 'N/A',
}

export interface PstParameterDescriptor<ParmName extends string> {
  id: string;
  name: ParmName;
  data: DataType;
  value: CardinalityType;
  optional: ((part: Record<ParmName, string>) => boolean) | boolean;
  default?: (part: Record<ParmName, string>) => string | undefined;
}
export type PstParameterSet<ParmName extends string> = Record<string, PstParameterDescriptor<ParmName>>;
export type PstDeviceList<ParmName extends string> = Record<ParmName, string | Record<string, unknown>>[];

export async function load<PSTParmName extends string, ParmName extends string>(
  adiLocale: string,
  configRequest: Promise<ToolsConfigJson<unknown> | null>,
  pstRequest: Promise<PSTData | null>,
  pstParameters: PstParameterSet<PSTParmName>,
  configParameters: string[],
  logger: Logger,
  staticParts?: StaticPartList,
  logOmissions?: boolean,
): Promise<PstDeviceList<ParmName> | null> {
  let index = new PartIndex();
  return configRequest
    .then((config) => {
      if (!config) {
        throw 'No config data';
      }
      logger.time('partsCfgTime');
      collectConfigData(adiLocale, config, configParameters, index, staticParts);
      logger.timeEnd('partsCfgTime');
      return config;
    })
    .then((config) => {
      return pstRequest.then((parts) => {
        if (!parts) {
          throw 'No PST Parts Data';
        }
        logger.time('partsPstTime');
        collectPstData(config.cdpsource.ignoreInPDB, parts, index);
        logger.timeEnd('partsPstTime');
        return index;
      });
    })
    .then((index) => {
      logger.time('partsInterpretTime');
      let parts = interpretPartIndex(index, pstParameters, configParameters, logger, logOmissions);
      logger.timeEnd('partsInterpretTime');
      if (logOmissions) {
        logger.log('Parts:');
        logger.log(parts?.map((item) => item['partNumber']));
      }
      return parts;
    })
    .catch((error) => {
      logger.error(`Error collecting part Data: ${error}`);
      return null;
    });
}

// The PartIndex keeps all the parameter data organized first by part, then by source, then by parameter
class PartIndex {
  data: Record<string, Record<string, PSTParameterSet>>;

  constructor() {
    this.data = {};
  }

  getIds(): string[] {
    return Object.keys(this.data);
  }

  addPart(id: string, source: string, value: PSTParameterSet): void {
    if (!(id in this.data)) {
      this.data[id] = {};
    }
    (this.data[id] as Record<string, PSTParameterSet>)[source] = value;
  }

  getMergedPart(id: string, configParms: string[]): PSTParameterSet {
    // merged part will have properties partId, the "numeric" properties, and
    // may also have polesZeroes or special for additional properties.
    let mergedPart: Record<string, PSTParameter> = { partId: { value: [id], displayValue: id } };
    let part = this.data[id] as Record<string, PSTParameterSet>;
    let partIsLive = false;
    let proptest = /^[s0-9-][0-9]+$/;

    // Walk the properties of the part
    // Override order: pdb overrides static, but config overrides all
    if ('static' in part) {
      for (let propId in part['static']) {
        if (proptest.test(propId)) {
          mergedPart[propId] = part['static'][propId] as PSTParameter;
        }
      }
      partIsLive = true;
    }
    if ('pst' in part) {
      const pstParameterSet = part['pst'] as PSTParameterSet;
      const genericPart = pstParameterSet['0'] as PSTParameter;
      const generic = genericPart.generic ?? id.toLowerCase();
      mergedPart['generic'] = { value: [generic] };
      for (let propId in part['pst']) {
        if (proptest.test(propId)) {
          let isok = true;
          if (propId in mergedPart) {
            // Don't overwrite a parameter that might be well-formed with one that isn't
            isok = this.parameterWellFormed(part['pst'][propId] as PSTParameter);
          }
          if (isok) {
            mergedPart[propId] = part['pst'][propId] as PSTParameter;
          }
        }
      }
      partIsLive = true;
    }
    if ('config' in part) {
      for (let propId in part['config']) {
        if (proptest.test(propId)) {
          let isok = true;
          if (propId in mergedPart) {
            // Don't overwrite a parameter that might be well-formed with one that isn't.
            isok = this.parameterWellFormed(part['config'][propId] as PSTParameter);
          }
          if (isok) {
            mergedPart[propId] = part['config'][propId] as PSTParameter;
          }
        }
      }
      partIsLive = true;
    }

    for (let parm of ['spice'].concat(configParms)) {
      let parmParts = parm.split(':');
      let inParm = parmParts[0] as string;
      let outParm = parmParts[parmParts.length - 1] as string; //Same as inParm if there is no colon
      if (partIsLive && inParm in part) {
        mergedPart[outParm] = part[inParm] as PSTParameter;
      }
    }
    return mergedPart;
  }

  parameterWellFormed(parameter: PSTParameter): boolean {
    return (
      typeof parameter.value !== 'undefined' &&
      parameter.value !== null &&
      typeof parameter.value.length !== 'undefined' &&
      parameter.value.length !== 0 &&
      parameter.value[0] !== ''
    );
  }
}
